"use client";

import React, { useState, useEffect } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, navigationMenuTriggerStyle } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
const DEFAULT_HERO_BG = "https://humble-drake.10web.cloud/wp-content/uploads/2026/02/Chen.webp";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex gap-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-9 w-20 bg-white/20 animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className={`${navigationMenuTriggerStyle()} bg-transparent text-white hover:bg-white/20 hover:text-white focus:bg-white/20 focus:text-white data-[active]:bg-white/20 data-[state=open]:bg-white/20 font-heading text-sm uppercase tracking-wider px-4 py-2 border-none shadow-none`}>
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const {
    menuItems
  } = useMenu();
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-[60] bg-[var(--color-primary)] text-white flex flex-col p-6 md:hidden">
      <div className="flex justify-end mb-8">
        <Button variant="ghost" size="icon" onClick={onClose} className="text-white hover:bg-white/10">
          <X className="size-8" />
        </Button>
      </div>
      <nav className="flex flex-col gap-6 items-center text-center">
        {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="text-2xl font-serif hover:text-[var(--color-ring)] transition-colors" onClick={onClose} data-index={i}>
            {item.label}
          </Link>)}
        <Button asChild className="mt-8 bg-[var(--color-ring)] text-white hover:bg-[var(--color-ring)]/90 w-full max-w-xs">
          <Link to="/ContactPage" onClick={onClose}>
            Contact Us
          </Link>
        </Button>
      </nav>
    </div>;
}
interface HeroProps {
  backgroundImage?: string;
}
export default function Hero({
  backgroundImage
}: HeroProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const heroBgImage = backgroundImage || DEFAULT_HERO_BG;
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide(prev => prev === 0 ? 1 : 0);
    }, 5000);
    return () => clearInterval(interval);
  }, []);
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 500);
    return () => clearTimeout(timer);
  }, []);
  return <section id="hero" className="relative w-full h-[100vh] overflow-hidden bg-[var(--color-primary)] bg-cover bg-center bg-no-repeat bg-[image:var(--hero-bg)] !bg-[url(https://humble-drake.10web.cloud/wp-content/uploads/2026/02/Chen.webp)]" style={{
    "--hero-bg": `url(${heroBgImage})`
  } as React.CSSProperties}>
      <div className="absolute inset-0 z-0">
        <div className={`absolute inset-0 bg-cover bg-center bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1553131/img/hero_bg_1.png)] transition-transform duration-[10000ms] ease-out ${currentSlide === 0 ? "scale-110 opacity-100" : "scale-100 opacity-100"}`} />
        
        <div className={`absolute inset-0 bg-cover bg-center bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1553131/img/hero_bg_2.png)] transition-transform duration-1000 ease-in-out ${currentSlide === 1 ? "translate-y-0" : "-translate-y-full"}`} />

        <div className="absolute inset-0 bg-[var(--color-primary)]/40 mix-blend-multiply" />
        <div className="absolute inset-0 bg-black/20" />
      </div>

      <header className="absolute top-0 left-0 right-0 z-50 flex items-center justify-between px-6 py-6 md:px-12">
        <div className="flex-shrink-0">
          <Link to="/HomePage">
            <img src="https://humble-drake.10web.cloud/wp-content/uploads/2026/02/cropped-SayKen-Tours-Final-Logo-1-e1728386750833.png" alt="Milk + Honey Logo" className="h-7 w-auto object-contain" />
          </Link>
        </div>

        <MenuProvider menu_id="17" className="hidden md:block">
          <HeaderMenuConsumer />
        </MenuProvider>

        <div className="hidden md:flex flex-shrink-0">
          <Button asChild className="bg-[var(--color-ring)] text-white hover:bg-[var(--color-ring)]/90 font-bold shadow-lg border border-white/20">
            <Link to="/ContactPage">Contact Us</Link>
          </Button>
        </div>

        <div className="md:hidden">
          <Button variant="ghost" size="icon" className="text-white hover:bg-white/20" onClick={() => setMobileMenuOpen(true)}>
            <Menu className="size-8" />
            <span className="sr-only">Open menu</span>
          </Button>
        </div>
      </header>

      <MenuProvider menu_id="17">
        <MobileMenu isOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />
      </MenuProvider>

      <div className="relative z-10 flex flex-col items-center justify-center h-full px-4 text-center text-white">
        <div className={`max-w-4xl mx-auto transition-all duration-1000 ease-out transform ${isLoaded ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}`}>
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold font-serif tracking-tight mb-4 drop-shadow-lg leading-tight">
            Unforgettable Memories of Israel, One Tour at a Time
          </h1>
          <p className="text-lg md:text-2xl font-sans italic text-white/90 mb-8 drop-shadow-md max-w-2xl mx-auto">
            Experience the land of milk and honey like never before
          </p>
          
          <div className="md:hidden mt-6">
             <Button asChild size="lg" className="bg-[var(--color-ring)] text-white hover:bg-[var(--color-ring)]/90 font-bold shadow-lg w-full max-w-xs">
              <Link to="/ContactPage">Start Your Journey</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}